import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../context/AppContext';

/**
 * DiaDataTableServerSideWrapperProps
 */
interface DiaDataTableServerSideWrapperProps {
  tableId: string;
  apiUrl?: string;
  header: any;
  options?: any;
  columns?: any;
  tableClass?: string;
  checkboxes?: boolean;
  customService?: any
  customServiceName?: string;
}

/**
 * how long the web component gets to produce a table before the wrapper calls
 * the mount failed. the component's own componentDidLoad swallows every error,
 * so from the outside a dead mount and a slow backend look identical at first
 */
const DATATABLE_TIMEOUT_MS = 8000;

/**
 * on some cold loads the component's init dies silently before it ever issues
 * a data request: the bundle's I18N decorator swaps the class-shared proto.I18N
 * against a fresh, not-yet-initialized instance on every mount, and a
 * componentDidLoad that catches the swap mid-flight dies on the uninitialized
 * singleton ("please initialize the I18N interface!"). that cannot be fixed
 * from outside the bundle, and every remount runs the same gauntlet again --
 * so the shell retries exactly once and then hands control to the user, whose
 * retry has always succeeded in practice. more or faster automatic remounts
 * make things worse: each one swaps the shared proto again mid-flight
 */
const DATATABLE_AUTO_RETRIES = 1;

/**
 * setAttributeIfChanged
 *
 * the a11y retrofit below re-runs on every header mutation; writing the same
 * value again would fire the observer once more and loop forever
 * @param element
 * @param name
 * @param value
 */
const setAttributeIfChanged = (element: Element, name: string, value: string) => {
  if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
};

/**
 * DataTableRetrofitLabels
 */
interface DataTableRetrofitLabels {
  perPage: string;
  empty: string;
  emptySearchHint: string;
}

/**
 * enhanceDataTableA11y
 *
 * the per-page select ships without an accessible name; it is retrofitted here
 * and re-applied by the observer -- page/per-page rebuild the header DOM.
 * column sorting stays untouched: every table through this wrapper is
 * server-side, and the component's columns.sort() only reorders the rows of
 * the loaded page while the request never carries a sort parameter -- a
 * sorter that claims to sort the whole set would lie
 * @param host
 * @param labels
 */
const enhanceDataTableA11y = (host: HTMLElement, labels: DataTableRetrofitLabels) => {
  const perPageSelect = host.querySelector("select.datatable-selector");

  if (perPageSelect) {
    setAttributeIfChanged(perPageSelect, "aria-label", labels.perPage);
  }

  // the component overwrites options.labels with its own bundled i18n after the
  // options merge, so its "Keine Einträge gefunden!" cannot be configured away;
  // the shell rewrites the cell and, while a search is active, also names the
  // way back to the full list. the td selector matters: the wrapper div carries
  // the same class, and rewriting that would wipe the whole table. the equality
  // guard keeps the observer loop-free
  const emptyCell = host.querySelector("td.datatable-empty");

  if (emptyCell) {
    const searchInput = host.querySelector("input[type='search']") as HTMLInputElement | null,
      emptyText = searchInput?.value.trim()
        ? `${labels.empty}. ${labels.emptySearchHint}`
        : labels.empty;

    if (emptyCell.textContent !== emptyText) {
      emptyCell.textContent = emptyText;
    }
  }
};

/**
 * DataTable
 *
 * state shell around the dia-datatable web component. the component reports
 * neither progress nor failure -- when its init dies, it leaves an empty
 * <!----> husk behind forever. the shell watches the DOM instead: loading
 * until a table shows up, an error panel with retry when it never does.
 *
 * @param tableId
 * @param apiUrl
 * @param header
 * @param options
 * @param columns
 * @param tableClass
 * @param checkboxes
 * @param customService
 * @param customServiceName
 * @constructor
 */
export const DataTable: React.FC<DiaDataTableServerSideWrapperProps> = ({
  tableId,
  apiUrl,
  header,
  options,
  columns,
  tableClass,
  checkboxes = false,
  customService,
  customServiceName
}) => {
  const { I18N } = useContext(AppContext);
  // the host is state, not a ref: after a retry the fresh dia-datatable mounts
  // one render AFTER the failed state cleared, so the watcher effect has to
  // re-fire on node attach -- a plain ref would leave it armed against nothing
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);

  // the web component reads this global once in its componentDidLoad; it must
  // be assigned before that runs, i.e. during render, not in an effect
  // @ts-ignore
  window.DIA_DATATABLE_SERVICE = customService;

  useEffect(() => {
    if (!host) {
      return;
    }

    // a dead init can leave a bare <table> husk behind (no thead, no rows);
    // only a table with rendered header cells counts as a successful mount
    const ready = () => !!host.querySelector('table thead th');

    if (ready()) {
      setState('ready');
      return;
    }

    const observer = new MutationObserver(() => {
      if (ready()) {
        setState('ready');
        observer.disconnect();
        clearTimeout(timeout);
      }
    });
    observer.observe(host, { childList: true, subtree: true });

    const timeout = setTimeout(() => {
      observer.disconnect();

      if (ready()) {
        setState('ready');
        return;
      }

      // silent retry first: the remount stays in the loading state, only a
      // mount that failed repeatedly surfaces the error panel
      if (attempt < DATATABLE_AUTO_RETRIES) {
        setAttempt(attempt + 1);
      } else {
        setState('failed');
      }
    }, DATATABLE_TIMEOUT_MS);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [host, attempt]);

  useEffect(() => {
    if (!host || state !== 'ready') {
      return;
    }

    const enhance = () => enhanceDataTableA11y(host, {
      perPage: I18N.t("Einträge pro Seite"),
      empty: I18N.t("Keine Einträge gefunden"),
      emptySearchHint: I18N.t("Setzen Sie die Suche zurück, um alle Einträge zu sehen.")
    });

    enhance();

    // the table re-renders its header on page/per-page and drops the retrofit
    // attributes with it. re-applying was observer-driven once, but a
    // MutationObserver interleaves as a microtask with the component's own
    // table build and the mutual rewrites can lock the main thread (observed
    // as a hard freeze right after the data response); a slow idempotent poll
    // re-applies the retrofit just as reliably and cannot ping-pong
    const poll = setInterval(enhance, 1500);

    return () => {
      clearInterval(poll);
    };
    // I18N is stable for a given locale, and the locale change already forces a rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, state]);

  if (state === 'failed') {
    return (
      <div data-datatable-error role="alert">
        <p>{ I18N.t("Die Tabelle konnte nicht geladen werden.") }</p>
        <button
          data-secondary
          data-small
          onClick={ () => {
            // back to loading in the same render that remounts the component,
            // otherwise the effect sees no host and the watchdog never arms
            setState('loading');
            setAttempt((current) => current + 1);
          } }
        >
          { I18N.t("Erneut versuchen") }
        </button>
      </div>
    );
  }

  /**
   * render
   */
  return (
    <>
      { /* the live region exists from the first render on and only its text
           toggles: a region inserted together with its content is usually
           not announced at all */ }
      <p data-datatable-loading aria-live="polite">
        { state === 'loading' ? I18N.t("Daten werden geladen …") : "" }
      </p>
      <dia-datatable
        // remounting is the only reset the component understands: attempt in the
        // key forces a fresh componentDidLoad after a failed init
        key={ `${tableId}-${attempt}` }
        // @ts-ignore
        ref={ setHost }
        table-id={ tableId }
        api-url={ apiUrl }
        table-class={ tableClass }
        // @ts-ignore
        header={ JSON.stringify(header) }
        options={ JSON.stringify(options) }
        columns={ JSON.stringify(columns, function(_key, value) {
          if (_key === "render" || _key === "renderHeader") {
            return value.toString();
          } else {
            return value;
          }
        }) }
        server-side="true"
        checkboxes={ checkboxes ? "true" : undefined }
        custom-service-name={ customServiceName }
      >
      </dia-datatable>
    </>
  );
};

/**
 * DataTableColumnsEncode
 * @param columns
 * @constructor
 */
export const DataTableColumnsEncode = (columns)=> {
  return JSON.stringify(columns, function(_key, value) {
    if (_key === "render" || _key === "renderHeader") {
      return value.toString();
    } else {
      return value;
    }
  });
}

/**
 * DataTableColumnsDecode
 * @param columns
 * @constructor
 */
export const DataTableColumnsDecode = (columns)=> {
  return JSON.parse(columns, function (_key, value) {
    if (_key === "render" || _key === "renderHeader") {
      // eslint-disable-next-line
      return new Function('return ' + value)();
    }

    return value;
  });
}

/**
 * DataTableColumnsDeepCopy
 * @param columns
 * @constructor
 */
export const DataTableColumnsDeepCopy = (columns)=>  {
  return DataTableColumnsDecode(DataTableColumnsEncode(columns));
}
